import { Agent } from './agent';
import {
  ListingType,
  ListingTypeMap,
  ListPriceMove,
  SortBy
} from '../classes/enums';
import { DetailStub } from '../components/listing-details/detail-section/detail-section.component';
import { IDemographicsResponse, Demographics } from './demographics';
import OpenHouse from './open-house';
import { Neighbourhood } from './neighbourhood';
import { StreetTypes } from '../app-config.constants';
import { environment } from '../../environments/environment-wrapper';
import { ResidentialExpandedDetailSections } from './residential-expanded-detail-sections';
import { Helper } from './helper';
import { CSSFlexOrder } from '../../app/classes/interfaces';

export class Listing implements CSSFlexOrder {
  constructor(
    listingStub?: any,
    favouriteListings?: string[],
    isSuggestedListing?: boolean
  ) {
    if (listingStub) {
      Object.keys(listingStub).forEach(key => {
        this[key] = listingStub[key];
      });

      this.fullAddress =
        [this.address, this.city, this.province].join(', ') +
        ' ' +
        this.postalCode;

      // Set constructor values here
      if (ListingTypeMap.house.indexOf(this.listingTypeId) >= 0) {
        this.isHouse = true;
        this.listingTypeLong = 'House';
      } else if (ListingTypeMap.townhouse.indexOf(this.listingTypeId) >= 0) {
        this.isTownhouse = true;
        this.listingTypeLong = 'Townhouse';
      } else if (ListingTypeMap.condo.indexOf(this.listingTypeId) >= 0) {
        this.isCondo = true;
        this.listingTypeLong = 'Condo';
      } else if (ListingTypeMap.rental.indexOf(this.listingTypeId) >= 0) {
        this.isLeaseOrRental = false;
        this.listingTypeLong = 'Rental';
      } else if (
        ListingTypeMap.house.indexOf(this.listingTypeId) !== -1 ||
        ListingTypeMap.townhouse.indexOf(this.listingTypeId) !== -1
      ) {
        this.isHouseTownhome = true;
        this.listingTypeLong = 'Townhome';
      }

      const leaseOrRentalTypes = [
        ListingType.Rental,
        ListingType.CommercialLease,
        ListingType.Lease,
        ListingType.VacationRental
      ];
      this.isLeaseOrRental =
        leaseOrRentalTypes.indexOf(this.listingTypeId) >= 0;

      if (this.halfBaths == '0') {
        this.halfBaths = null;
      }
      if (this.baths) {
        this.totalBaths = this.halfBaths
          ? `${this.baths} + ${this.halfBaths}`
          : '' + this.baths;
      }
      this.totalBeds = this.beds ? '' + this.beds : null;
      this.id = this.listingId;

      if (favouriteListings && favouriteListings.length) {
        this.isFavourite = favouriteListings.indexOf(this.listingId) >= 0;
      }

      // This little hack is needed in order to call Agent's constructor to set flag
      if (this.agent) {
        this.agent = new Agent(this.agent);
      }
      if (this.coAgent) {
        this.coAgent = new Agent(this.coAgent);
      }

      // TODO: dont need this after they update their code to remove domains
      const officeProfileUrlRegex = /https:\/\/www\.remax\.ca/g;
      this.detailUrl = this.detailUrl
        ? Helper.prependIndividualPrefix('/', this.detailUrl.replace(officeProfileUrlRegex, ''))
        : '';

      this.getFavouriteIconToUse();
      this.getVirtualTourIconToUse();
      this.getVideoIconToUse();
      this._setStreetTypeLong();
      this._setPlaceholderImage();
      this._normalizeIDXDisclosure();
      this._isFarm();

      if (ListingTypeMap.commercial.indexOf(this.listingTypeId) !== -1) {
        this.jsonLdSchema = this._setCommercialSchema(isSuggestedListing);
      } else {
        this.jsonLdSchema = this._setLuxuryResidentialSchema(
          isSuggestedListing
        );
      }

      if (this.hasOpenHouse) {
        const openHouseSERPs = this.openHouses.map(function(house) {
          return {
            '@type': 'Event',
            description: 'Open House',
            name: 'Open House - ' + house.timeDescription,
            startDate: house.startTime,
            location: {
              '@type': 'Place',
              name: 'Open House',
              address: {
                '@type': 'PostalAddress',
                streetAddress: this.fullAddress,
                addressLocality: this.city,
                addressRegion: this.province,
                postalCode: this.postalCode
              }
            },
            image: this.imageUrls[0],
            endDate: house.endTime,
            performer: {
              '@type': 'organization',
              name: 'RE/MAX'
            },
            offers: {
              '@type': 'Offer',
              name: 'Open House',
              url: environment.cdnServerUrl.slice(0, -1) + this.detailUrl,
              price: this.listPrice,
              validFrom: house.startTime,
              validThrough: house.endTime,
              priceCurrency: 'CAD',
              availability: 'LimitedAvailability'
            }
          };
        }, this);
        this.jsonLdSchema[0]['@graph'] = this.jsonLdSchema[0]['@graph'].concat(
          openHouseSERPs
        );
      }

      if (this.hasVirtualOpenHouse) {
        const openHouseSERPs = this.openHouses.map(function(house) {
          return {
            '@type': 'Event',
            description: 'Virtual Open House',
            name: 'Open House - ' + house.timeDescription,
            startDate: house.startTime,
            location: {
              '@type': 'Place',
              name: 'Virtual Open House',
              address: {
                '@type': 'PostalAddress',
                streetAddress: this.fullAddress,
                addressLocality: this.city,
                addressRegion: this.province,
                postalCode: this.postalCode
              }
            },
            image: this.imageUrls[0],
            endDate: house.endTime,
            performer: {
              '@type': 'organization',
              name: 'RE/MAX'
            },
            offers: {
              '@type': 'Offer',
              name: 'Virtual Open House',
              url: environment.cdnServerUrl.slice(0, -1) + this.detailUrl,
              price: this.listPrice,
              validFrom: house.startTime,
              validThrough: house.endTime,
              priceCurrency: 'CAD',
              availability: 'LimitedAvailability'
            }
          };
        }, this);
        this.jsonLdSchema[0]['@graph'] = this.jsonLdSchema[0]['@graph'].concat(
          openHouseSERPs
        );
      }

      this.openGraphContent = {
        bathroom: this.baths,
        bedroom: this.beds,
        area: this.sqFtSearch,
        propertyType: ListingType[this.listingTypeId],
        propertyAddress: this.fullAddress
      };
    }
    if (
      this.communityName &&
      this.city &&
      this.communityName.toLowerCase() === this.city.toLowerCase()
    ) {
      this.communityName = null;
    }
  }

  setDemographics(data: IDemographicsResponse) {
    this.demographics = new Demographics(data);
  }

  id: string; // Set to ListingId just to share a similar property to school

  flexOrder: number;

  listingId: string = '';
  isRemaxListing: boolean;
  isLuxury: boolean;
  acreage: string;
  active: boolean;
  address: string;
  agentIncorporation: string;
  airConditioning: boolean;
  area: string;
  age: string;
  basement: string;
  basementFinished: string;
  bath2: string;
  baths: number;
  fullBaths: string;
  halfBaths: string;
  bed1Dim: string;
  bed2Dim: string;
  bed3Dim: string;
  bed4Dim: string;
  bed5Dim: string;
  beds: number;
  bedsBR: number;
  bedsBRPlus: number;
  boardName: string;
  bonusDim: string;
  breakfastDim: string;
  buildingName: string;
  city: string;
  communityName: string;
  contractConditions: string;
  cooling: boolean;
  country: string;
  county: string;
  denDim: string;
  description: string;
  diningRoomDim: string;
  driveway: string;
  exterior: string;
  familyRoomDim: string;
  condoFeesMain: number;
  fireplace: boolean;
  fireplaces: number;
  fireplaceType: string;
  formalDiningAreaDim: string;
  foundation: string;
  foyerDim: string;
  gameRoomDim: string;
  garage: boolean;
  garageSpaces: number;
  garageType: string;
  greatRoomDim: string;
  hasOpenHouse: boolean;
  hasVirtualOpenHouse: boolean;
  hasHomeEstimate: boolean;
  heating: string;
  heatingSource: string;
  kitchenDim: string;
  lastUpdated: Date;
  lat: number;
  laundryDim: string;
  listingDate: Date;
  status: string;
  listingStatus: string;
  listingTypeId: number;
  listPrice: number = 0;
  listPriceMove: ListPriceMove;
  livingRoomDim: string;
  lng: number;
  lotSizeRaw: string;
  lotSizeSearch: number;
  marketPrice: boolean;
  masterBath: string;
  masterBedDim: string;
  masterBedroomFirstFloor: boolean;
  mediaRoomDim: string;
  mlsNum: string;
  modelType: string;
  occupancy: string;
  officeDenDim: string;
  openHouses: OpenHouse[] = [];
  virtualOpenHouses: OpenHouse[] = [];
  otherRoom1Dim: string;
  otherRoom2Dim: string;
  ownershipType: string;
  parkingSpaces: number;
  partialBathrooms: string;
  petPolicy: string;
  pool: boolean;
  postalCode: string;
  province: string;
  videoIconToUse?: string;
  virtualTourIconToUse: string;
  remaxAgentId: string;
  remaxAgentName: string;
  remaxAgentSourceSystemId: string;
  remaxCoAgentId: string;
  remaxCoAgentName: string;
  remaxCoAgentSourceSystemId: string;
  virutalTourIconToUse?: string;
  remaxOfficeId: string;
  remaxOfficeName: string;

  roof: string;
  rooms: number;
  schoolDistrict: string;
  services: string;
  sewer: string;
  source: string; // Listing Source ID
  sqFtRaw: string;
  sqFtSearch: number = 0;
  pricePerSqFt: number;
  stories: number;
  style: string;
  subDivision: string;
  taxAmount: number;
  taxYear: number;
  totalAcres: number;
  transactionType: string;
  utilityDim: string;
  videoUrl: string;
  virtualTourUrl: string;
  waterfront: boolean;
  waterview: boolean;
  normalizedCity: string;

  uaid: string;
  uuaid: string;

  agent: Agent;
  coAgent: Agent;
  imageUrls: Array<string>;
  features: { [key: string]: Array<string> };

  detailUrl: string;
  redirectUrl?: string;

  // Commercial Only
  primaryBusinessType?: string;

  // Front end specific
  hasBeenViewed: boolean = false;
  isFavourite: boolean = false;
  selected: boolean;

  // Missing
  headline: string; // Single Family in Wellesley

  // Custom properties for logic purposes
  isHouse: boolean = false;
  isTownhouse: boolean = false;
  isCondo: boolean = false;
  isCommercial: boolean = false;
  isHouseTownhome: boolean = false;
  isLeaseOrRental: boolean = false;
  listingTypeLong: string;
  totalBaths: string;
  totalBeds: string;
  streetTypeLongAddress: string;
  fullAddress: string;
  idxDisclosure: string;
  isFarm: boolean;
  favouriteIconToUse: string; // Which favourite icon we want to use

  placeHolderImage: string; // Placeholder Image URL in case retrieving the listing image fails

  highLevelDetailRows: Array<DetailStub>;
  keyFactRows: Array<DetailStub>;
  roomDetailRows: Array<DetailStub>;
  insideRows: Array<DetailStub>;
  outsideRows: Array<DetailStub>;
  additionalFeatureRows: Array<DetailStub>;

  residentialExpandedDetailSections = new ResidentialExpandedDetailSections();

  jsonLdSchema: object[] = [];
  breadcrumbSchema: object[] = [];

  demographics: Demographics;
  neighbourhood: Neighbourhood;

  openGraphContent: {
    bedroom: number;
    bathroom: number;
    area: number;
    propertyType: string;
    propertyAddress: string;
  }; // For Ask Avenue - {“propertyAddress":"1455 Benzer Crescent,, Kelowna, BC V1V 2Y1”, "bathroom":"3.00”, "bedroom":"5”, "area":"3613”, "propertyType":"SingleFamily”}

  // off market listings based properties
  propertyId?: string;
  isBlacklisted?: boolean;
  isOffMarket?: boolean;

  get isResidential(): boolean {
    return !this.isLuxury && !this.isCommercial && !this.isOffMarket;
  }

  static trackBy(index, listing: Listing) {
    return listing.listingId;
  }

  static setListingFlexOrder(listings: Listing[]) {
    for (let i = 0; i < listings.length; i++) {
      listings[i].flexOrder = 1;
    }
  }

  static sortBy(
    o1: Listing,
    o2: Listing,
    sortByValue: SortBy,
    isPricePerSqft: boolean = false
  ) {
    switch (sortByValue) {
      case SortBy.OLDEST:
        if (o1.listingDate > o2.listingDate) return 1;
        else if (o1.listingDate < o2.listingDate) return -1;
        return 0;
      case SortBy.PRICE_HIGH_TO_LOW:
        if (isPricePerSqft) {
          if (o1.pricePerSqFt < o2.pricePerSqFt) return 1;
          else if (o1.pricePerSqFt > o2.pricePerSqFt) return -1;
          return 0;
        } else {
          if (o1.listPrice < o2.listPrice) return 1;
          else if (o1.listPrice > o2.listPrice) return -1;
          return 0;
        }
      case SortBy.PRICE_LOW_TO_HIGH:
        if (isPricePerSqft) {
          if (o1.pricePerSqFt > o2.pricePerSqFt) return 1;
          else if (o1.pricePerSqFt < o2.pricePerSqFt) return -1;
          return 0;
        } else {
          if (o1.listPrice > o2.listPrice) return 1;
          else if (o1.listPrice < o2.listPrice) return -1;
          return 0;
        }
      default:
        if (o1.listingDate < o2.listingDate) return 1;
        else if (o1.listingDate > o2.listingDate) return -1;
        return 0;
    }
  }

  /**
   * this is used on the favourites gallery where you may have listings that have price per sqft mixed
   * with listings that have regular price
   * @param {Listing} o1
   * @param {Listing} o2
   * @param {SortBy} sortByValue
   */
  static sortByMixed(o1: Listing, o2: Listing, sortByValue: SortBy) {
    const priceO1 = o1.pricePerSqFt ? o1.pricePerSqFt : o1.listPrice;
    const priceO2 = o2.pricePerSqFt ? o2.pricePerSqFt : o2.listPrice;
    switch (sortByValue) {
      case SortBy.OLDEST:
        if (o1.listingDate > o2.listingDate) return 1;
        else if (o1.listingDate < o2.listingDate) return -1;
        return 0;
      case SortBy.PRICE_HIGH_TO_LOW:
        if (priceO1 < priceO2) return 1;
        else if (priceO1 > priceO2) return -1;
        return 0;
      case SortBy.PRICE_LOW_TO_HIGH:
        if (priceO1 > priceO2) return 1;
        else if (priceO1 < priceO2) return -1;
        return 0;
      default:
        if (o1.listingDate < o2.listingDate) return 1;
        else if (o1.listingDate > o2.listingDate) return -1;
        return 0;
    }
  }

  getFavouriteIconToUse() {
    this.favouriteIconToUse = this.isFavourite
      ? '/assets/icons/heart/heart_icon_red@2x.svg'
      : this.isCommercial || this.isLuxury
      ? this.isLuxury
        ? '/assets/icons/heart/heart_icon_white@2x.svg'
        : '/assets/icons/heart/heart_icon_commercial.svg'
      : '/assets/icons/heart/heart_icon_accessible@2x.svg';
  }

  getVideoIconToUse() {
    this.videoIconToUse = this.isCommercial
      ? '/assets/icons/listing-details/more-to-see/video_icon_red_t@1x.svg'
      : this.isLuxury
      ? '/assets/icons/listing-details/more-to-see/video_icon_white_t@1x.svg'
      : '/assets/icons/listing-details/more-to-see/video_icon_blue_t@1x.svg';
  }

  getVirtualTourIconToUse() {
    this.virtualTourIconToUse = this.isCommercial
      ? '/assets/icons/listing-details/more-to-see/virtual_tour_icon_red_t2@1x.svg'
      : this.isLuxury
      ? '/assets/icons/listing-details/more-to-see/virtual_tour_icon_white_t2@1x.svg'
      : '/assets/icons/listing-details/more-to-see/virtual_tour_icon_blue_accessible_t2@1x.svg';
  }

  /*
   * The content of idxDisclosure attribute contains html tags and some programming langugage related functions
   * The method removes html tags from string and replaces multiple spaces with single one also replaces @Year@ with current year
   * and @State@ with two character province name
   */
  private _normalizeIDXDisclosure() {
    if (this.idxDisclosure) {
      // To remove html tags,  Copyright and ©
      const htmlTagRegex = RegExp('<[^>]*>|$', 'g');
      // Replace string '@Year@' with value of current year
      const currentYearRemax = RegExp('@Year@', 'g');
      // To replace multiple spaces with a single one
      const singleSpaceRegex = RegExp('\\s{2,}', 'g');
      // To replace string '@State@' with actual province name
      const province = RegExp('@State@', 'g');

      this.idxDisclosure = this.idxDisclosure
        .replace(htmlTagRegex, '')
        .replace(singleSpaceRegex, ' ')
        .replace(currentYearRemax, String(new Date().getFullYear()))
        .replace(province, this.province)
        .trim();
    }
  }

  private _setStreetTypeLong() {
    // Regex to grab the street type (RD, AVE, etc, second last or last word in address)
    const streetTypeRegex = /^(.*?)([a-zA-Z]+)(\s(N|E|S|W){1,2})?\s*$/;
    const streetTypeShort = streetTypeRegex.exec(this.address);
  
    // Checks if address has a street type
    if (streetTypeShort && streetTypeShort[2] && streetTypeShort[2] !== "") {
      // Checks if street type abbreviation is in mapping
      if (StreetTypes[streetTypeShort[2].toUpperCase()]) {
        const streetTypeLong = StreetTypes[streetTypeShort[2].toUpperCase()];
        
        // Replaces short street type with long street type (AVE -> Avenue) and adds direction 
        this.streetTypeLongAddress = this.address.replace(streetTypeRegex, `$1${streetTypeLong}$3`);
      } else {
        // Normalize the short street type and return it capitalized (ie. AVEnue -> Avenue or STReet -> Street)
        const normalizedStreetType = streetTypeShort[2].charAt(0).toUpperCase() + streetTypeShort[2].slice(1).toLowerCase();

        this.streetTypeLongAddress = this.address.replace(streetTypeRegex, `$1${normalizedStreetType}$3`);
      }
    } else {
      this.streetTypeLongAddress = this.address;
    }
  }

  // [MBL-3844] - Getting Property Type based on listingTypeId
  private _getPropertyType(listingTypeId, isSuggestedListing: boolean) {
    if (
      !isSuggestedListing &&
      ListingType.Condo.valueOf() === this.listingTypeId
    )
      return 'Apartment';

    return 'House';
  }

  private _isFarm() {
    if (ListingType.Farm.valueOf() === this.listingTypeId) {
      this.isFarm = true;
      this.primaryBusinessType = 'Farm';
    } else {
      this.isFarm = false;
    }
  }

  private _setPlaceholderImage() {
    if (this.isCommercial)
      this.placeHolderImage =
        '/assets/listing-default-image/commercial_placeholder_v2@1x.svg';
    else if (this.isLuxury)
      this.placeHolderImage =
        '/assets/listing-default-image/luxury_placeholder_v2@1x.svg';
    else
      this.placeHolderImage =
        '/assets/listing-default-image/residential_placeholder_v2@1x.svg';
  }

  private _setCommercialSchema(isSuggestedListing) {
    return [
      {
        '@context': 'http://schema.org',
        '@graph': [
          {
            '@type': 'Offer',
            price: this.listPrice,
            priceCurrency: 'CAD',
            url: this.detailUrl
          }
        ]
      }
    ];
  }

  private _setLuxuryResidentialSchema(isSuggestedListing) {
    return [
      {
        '@context': 'http://schema.org',
        '@graph': [
          {
            '@type': 'SingleFamilyResidence',
            address: {
              '@type': 'PostalAddress',
              streetAddress: this.fullAddress,
              addressLocality: this.city,
              addressRegion: this.province,
              postalCode: this.postalCode
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: this.lat,
              longitude: this.lng
            }
          },
          {
            '@type': this._getPropertyType(
              this.listingTypeId,
              isSuggestedListing
            ),
            numberOfRooms: this.rooms,
            address: {
              '@type': 'PostalAddress',
              streetAddress: this.fullAddress,
              addressLocality: this.city,
              addressRegion: this.province,
              postalCode: this.postalCode
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: this.lat,
              longitude: this.lng
            },
            description: this.description,
            photo: {
              '@type': 'ImageObject',
              url: this.imageUrls
            }
          },
          {
            '@type': 'Offer',
            price: this.listPrice,
            priceCurrency: 'CAD',
            url: this.detailUrl
          }
        ]
      }
    ];
  }
}

export enum FlagType {
  COMING_SOON = 'Coming Soon'
}
